import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Newspaper, Trash2, Send, ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Health Tip", "DermaGen Story", "Pharmacy News"];

const DARK_INPUT = "rounded-none border-white/20 bg-transparent text-white placeholder:text-white/40";

export default function BlogManager({ pin }) {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", category: "Health Tip", body: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await axios.get(`${API}/posts`);
    setPosts(res.data);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
  };

  const publish = async () => {
    setSaving(true);
    try {
      let image_path = null;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        fd.append("pin", pin);
        const up = await axios.post(`${API}/uploads`, fd);
        image_path = up.data.path;
      }
      await axios.post(`${API}/posts`, { ...form, image_path, pin });
      toast.success("Published — it's live in the News & Stories section");
      setForm({ title: "", category: "Health Tip", body: "" });
      clearImage();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (post_id) => {
    try {
      await axios.post(`${API}/posts/${post_id}/delete`, { pin });
      toast.success("Post deleted");
      await load();
    } catch {
      toast.error("Could not delete — please try again");
    }
  };

  return (
    <div className="mt-5 border border-white/15 bg-white/5 p-8" data-testid="blog-manager">
      <div className="flex items-start gap-5">
        <div className="border border-white/20 p-3 text-brand-green">
          <Newspaper className="h-6 w-6" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold">News & stories publisher</h3>
          <p className="mt-1 text-sm leading-relaxed text-white/60">
            Publish seasonal health tips and DermaGen stories to the website — with a photo if you like. New posts appear instantly in News & Stories.
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-2" data-testid="blog-controls">
          <div className="space-y-4">
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Post title"
              data-testid="blog-title-input"
              className={DARK_INPUT}
            />
            <select
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              data-testid="blog-category-select"
              className="h-10 w-full rounded-none border border-white/20 bg-transparent px-3 text-sm text-white"
            >
              {CATEGORIES.map((c) => <option key={c} value={c} className="text-black">{c}</option>)}
            </select>
            <Textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Write your tip or story… blank lines create paragraphs."
              rows={6}
              data-testid="blog-body-input"
              className={DARK_INPUT}
            />
            <div>
              <label
                className="flex cursor-pointer items-center gap-3 border border-dashed border-white/25 px-4 py-3 text-sm text-white/60 transition-colors duration-200 hover:border-brand-green hover:text-white"
                data-testid="blog-image-drop"
              >
                <ImagePlus className="h-5 w-5 shrink-0 text-brand-green" />
                <span className="truncate">{imageFile ? imageFile.name : "Attach a photo (optional) — JPG, PNG or WEBP, max 5MB"}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleFile}
                  data-testid="blog-image-input"
                />
              </label>
              {imagePreview && (
                <div className="relative mt-3 w-40" data-testid="blog-image-preview">
                  <img src={imagePreview} alt="Preview" className="h-24 w-40 rounded-lg object-cover" />
                  <button
                    onClick={clearImage}
                    data-testid="blog-image-remove"
                    aria-label="Remove image"
                    className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white transition-colors duration-200 hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            <Button
              onClick={publish}
              disabled={saving || !form.title.trim() || !form.body.trim()}
              data-testid="blog-publish-btn"
              className="rounded-none bg-brand-green px-6 font-heading font-semibold text-white hover:bg-brand-greendark disabled:opacity-50"
            >
              <Send className="mr-2 h-4 w-4" /> {saving ? "Publishing…" : "Publish post"}
            </Button>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/50">Published posts</p>
            <ul className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="blog-posts-list">
              {posts.map((p) => (
                <li key={p.post_id} className="flex items-center justify-between gap-3 border border-white/10 bg-white/5 px-4 py-3" data-testid={`blog-post-${p.post_id}`}>
                  {p.image_path && (
                    <img src={`${API}/files/${p.image_path}`} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{p.title}</p>
                    <p className="text-xs text-white/40">{p.category}</p>
                  </div>
                  <button
                    onClick={() => remove(p.post_id)}
                    data-testid={`blog-delete-${p.post_id}`}
                    aria-label={`Delete ${p.title}`}
                    className="shrink-0 p-2 text-white/40 transition-colors duration-200 hover:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
    </div>
  );
}
